/* eslint-disable react/no-children-prop */
import { memo, useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import PassengersCounterDisplay from "app/pages/.shared/PassengersCounterDisplay";
import { FormattedMessage, useIntl } from "react-intl";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import TravelDatesContainer from "app/pages/.shared/TravelDates/TravelDatesContainer";
import IconCalendar from "app/pages/.shared/IconCalendar";
import IconNegoBed from "app/pages/.shared/IconNegoBed";
import IconNegoDinner from "app/pages/.shared/IconNegoDinner";
import IconNegoPlane from "app/pages/.shared/IconNegoPlane";
import { getFlightNegotiatedLabelKey } from "app/utils/smartDPUtils";
import IconNegoTransfer from "app/pages/.shared/IconNegoTransfer";
import BookingBannerPlaceholderContainer from "app/pages/Booking/BookingBannerPlaceholder/BookingBannerPlaceholderContainer";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import Collapse from "rc-collapse";
import MyFlightDetail from "app/pages/Booking/MyFlightDetail";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import AppGlobalsContext from "app/AppGlobalsContext";
import OccupanciesDisplayLabel from "app/pages/.shared/OccupanciesDisplayLabel/OccupanciesDisplayLabel";
import NegociatedItem from "app/pages/FicheProduit/NegociatedList/NegociatedItem";
import TravelDurationDatesContainer from "app/pages/.shared/TravelDurationDates/TravelDurationDatesContainer";
import "./HolidaySummary.scss";
import {
	NEGOCIATED_TYPES,
	PRICE_CHANGE_STATUS,
	QUOTATION_CODE_STATUS,
	TRANSPORTATION_TYPES,
} from "app/constants";
import IconLocation from "app/pages/.shared/IconLocation";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import Timer from "app/pages/.shared/Timer";
import Badge from "app/pages/.shared/Badge";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import classNames from "classnames";
import IconNegoTransport from "app/pages/.shared/IconNegoTransport";
import { take } from "lodash";
import { useLocation } from "react-router-dom";
import IconDeparting from "app/pages/.shared/IconDeparting";
import isEmpty from "lodash/isEmpty";
import IconClockNotification from "app/pages/.shared/static/icons/IconClockNotification";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useFsFlag } from "@flagship.io/react-sdk";

const IMG_SIZES_DESKTOP = {
	xlarge: { height: 128, width: 340 },
	large: { height: 128, width: 340 },
};

const IMG_SIZES_MOBILE = {
	medium: { width: 800 },
	small: { width: 800 },
};

const NEGOS_TO_EXCLUDE = [
	NEGOCIATED_TYPES.SPECIAL_OFFER,
	NEGOCIATED_TYPES.ACCOMMODATION,
	NEGOCIATED_TYPES.BOARD,
	NEGOCIATED_TYPES.CAR_RENTAL,
	NEGOCIATED_TYPES.TRANSFER,
	NEGOCIATED_TYPES.FLIGHT,
];

const HolidaySummary = ({
	isSDP,
	booking = {},
	ficheProduit = {},
	showProductPicture,
	showFlightDetails,
	showTimer,
	showBookingBanner,
	hideQuoteOptions,
	luggageMaxCount = 0,
	quotationStatus,
	negotiatedOfSelectedOffer = [],
	activities = [],
	stressMarketingMsg = {},
	priceChangeData = {},
	quotationCode,
	flightOptions = {},
	flightOptionsFlightCode,
}) => {
	const { pathname } = useLocation();
	const flightDetailsCollapseRef = useRef(null);
	const datesDetailsCollapseRef = useRef(null);

	const hasQuoteFullPropertyWarning = quotationStatus === QUOTATION_CODE_STATUS.FULL_PROPERTY;

	const { track } = useAnalytics();
	const showStressMarketingMsgFlag = useFsFlag("stress_marketing_message_display", false);
	const showStressMarketingFs =
		showStressMarketingMsgFlag.getValue() && !hasQuoteFullPropertyWarning;

	const accommodation = booking.accommodation || {};
	const board = booking.board || {};
	const flight = booking.flight || {};
	const transfer = booking.transfer || {};
	const departureDate = booking?.departureDate;

	const isPaymentPage = pathname.includes("/payment");

	const getDepartureCity = str => {
		if (str) {
			const firstParenIndex = str.indexOf("(");
			return firstParenIndex !== -1 ? str.substring(0, firstParenIndex).trim() : str.trim();
		}
		return str;
	};
	const departureCity = getDepartureCity(booking?.departureCity?.label);
	const departureIATA = booking?.flight?.outbound?.legs[0]?.from?.iata;

	const intl = useIntl();
	const luggageAddedByUser = booking?.luggage;
	const flightLabelKey = getFlightNegotiatedLabelKey(flight, flightOptions, luggageAddedByUser);
	const flightNegociatedLabel = intl.formatMessage({ id: flightLabelKey });

	const tripAdvisorData = ficheProduit.tripAdvisor || {};
	const holidayCheckData = ficheProduit.holidaycheck || {};

	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const photo = ficheProduit.photos && ficheProduit.photos[0];

	// pour pp, les negotiated sont par type d'offre alors que pour sdp, non
	let negotiatedList = [];
	const {
		isMediumLevelMsg,
		stressMarketingLabel,
		level,
		stressMarketingShortLabel,
	} = stressMarketingMsg;

	useEffect(() => {
		if (
			level !== false &&
			level !== undefined &&
			stressMarketingLabel !== false &&
			stressMarketingLabel !== undefined &&
			showStressMarketingFs &&
			quotationCode
		) {
			track("stress_marketing_data", {
				level: level,
				message: stressMarketingLabel,
				...(stressMarketingShortLabel !== false &&
					stressMarketingShortLabel !== undefined && {
						shortMessage: stressMarketingShortLabel,
					}),
			});
		}
	}, [quotationCode]);

	// we can not use activeKey in this case
	//with defaultActiveKey when we switch pages, the 'flight' and/or 'dates' block(s) do not open automatically
	useEffect(() => {
		if (isPaymentPage && !isMobile) {
			if (flightDetailsCollapseRef?.current) {
				flightDetailsCollapseRef.current.setActiveKey(["0"]);
			}
			if (datesDetailsCollapseRef?.current) {
				datesDetailsCollapseRef.current.setActiveKey(["0"]);
			}
		}
	}, [
		flightDetailsCollapseRef?.current,
		isPaymentPage,
		isMobile,
		datesDetailsCollapseRef?.current,
	]);

	if (negotiatedOfSelectedOffer?.length > 0) {
		negotiatedList = negotiatedOfSelectedOffer;
	} else if (ficheProduit?.negotiated?.length > 0) {
		negotiatedList = ficheProduit.negotiated;
	}

	const negotiatedListToDisplay =
		(negotiatedList &&
			negotiatedList.filter(negotiated => {
				return !NEGOS_TO_EXCLUDE.includes(negotiated?.type);
			})) ||
		[];

	const holidaySummaryDetailsClass = classNames("holiday-summary__details", {
		"holiday-summary__details--loading": quotationStatus !== QUOTATION_CODE_STATUS.SUCCESS,
	});

	const geographyFirstLevel =
		ficheProduit?.geography?.levels[ficheProduit?.geography?.levels?.length - 1]?.label;
	const geographySecondLevel = take(
		ficheProduit?.geography?.levels,
		ficheProduit?.geography?.levels?.length - 1
	)
		?.map(geo => geo.label)
		?.join(", ");

	const showTimerOnProductPicture =
		showTimer && (ficheProduit.expiration?.hour > 0 || ficheProduit.expiration?.day > 0);

	const nonIncludedActivitiesList = activities.filter(activity => {
		return activity.guests.every(guest => guest.includedGuest === 0);
	});

	const productName = (
		<ProductNameWithCategory name={ficheProduit.name} category={ficheProduit.category} />
	);

	const showStressMarketingMsg = isMediumLevelMsg
		? showStressMarketingFs && priceChangeData?.priceChangeStatus !== PRICE_CHANGE_STATUS.UP
		: showStressMarketingFs && true;

	return (
		<div className="holiday-summary" data-testid="holiday-summary">
			{showBookingBanner && <BookingBannerPlaceholderContainer />}

			<div className={holidaySummaryDetailsClass}>
				{showProductPicture && photo && (
					<div className="holiday-summary__picture">
						{isMobile ? (
							<BackgroundProgressiveImg
								sizes={IMG_SIZES_MOBILE}
								src={photo.url}
								alt={photo.alt}
								thumbnail={photo.thumbnail}
								backgroundPosition={"center"}
								productUri={ficheProduit?.uri}
							/>
						) : (
							<ProgressiveImg
								sizes={IMG_SIZES_DESKTOP}
								src={photo.url}
								alt={photo.alt}
								resolution={resolution}
								thumbnail={photo.thumbnail}
								productUri={ficheProduit?.uri}
							/>
						)}

						{showTimerOnProductPicture && (
							<div
								className="holiday-summary__badge"
								data-testid="holiday-summary-badge"
							>
								<Badge>
									<Timer
										hour={ficheProduit.expiration?.hour}
										day={ficheProduit.expiration?.day}
										suffix={<FormattedMessage id="timer.left.label" />}
									/>
								</Badge>
							</div>
						)}
					</div>
				)}

				<div className="holiday-summary__title" data-testid="detail-resort">
					<FormattedMessage id="booking.holiday.summary.to.title" />
				</div>

				<ul className="holiday-summary__list">
					{departureCity && (
						<li
							className="holiday-summary__list-item"
							data-testid="detail-departureCity"
						>
							<IconDeparting />
							<p className="holiday-summary__departureCity">
								{departureCity}
								{departureIATA ? ` (${departureIATA})` : ""}
							</p>
						</li>
					)}

					<li
						className={`holiday-summary__list-item ${
							geographySecondLevel && geographySecondLevel.length > 0
								? "holiday-summary__list-items"
								: ""
						}`}
						data-testid="detail-location"
					>
						<IconLocation />
						<div>
							{ficheProduit?.geography ? (
								<>
									{geographyFirstLevel && (
										<p className="holiday-summary__geography-level--1">
											{geographyFirstLevel}
										</p>
									)}
									{geographySecondLevel && (
										<p className="holiday-summary__geography-level--2">
											{geographySecondLevel}
										</p>
									)}
								</>
							) : (
								<>
									{ficheProduit.region}, {ficheProduit.resort}
								</>
							)}
						</div>
					</li>

					{departureDate && !hasQuoteFullPropertyWarning && (
						<Collapse
							className="holiday-summary__collapse"
							ref={datesDetailsCollapseRef}
							expandIcon={({ isActive }) => {
								return (
									<IconArrowDown
										style={{
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="holiday-summary__collapse-header"
								header={
									<li className="holiday-summary__list-item">
										<IconCalendar />
										<TravelDatesContainer />
									</li>
								}
							>
								<div className="holiday-summary__collapse-content">
									<TravelDurationDatesContainer />
								</div>
							</Collapse.Panel>
						</Collapse>
					)}

					<li className="holiday-summary__list-item" data-testid="detail-travellers">
						<i className="icon icon--travellers" />
						{isSDP ? (
							<OccupanciesDisplayLabel occupancies={booking.occupancies} />
						) : (
							<PassengersCounterDisplay
								adults={booking.adults}
								children={booking.children}
								infants={booking.infants}
							/>
						)}
					</li>

					<li className="holiday-summary__list-item" data-testid="detail-product-name">
						<i className="icon icon--hotel" />
						<div className="holiday-summary__product-name">
							{productName}
							<ProductRatingContainer
								locationName={productName}
								tripAdvisor={tripAdvisorData}
								holidayCheck={{
									...holidayCheckData,
									size: "small",
								}}
							/>
						</div>
					</li>

					{!hideQuoteOptions && accommodation.items && accommodation.items.length > 0 && (
						<li
							className="holiday-summary__list-item"
							data-testid="detail-accommodation"
						>
							<IconNegoBed />
							<div>
								{accommodation.items.map((item, index) => {
									return <div key={index}>1 x {item.label}</div>;
								})}
							</div>
						</li>
					)}

					{!hideQuoteOptions && board.label && (
						<li className="holiday-summary__list-item" data-testid="detail-board">
							<IconNegoDinner />
							<div>{board.label}</div>
						</li>
					)}

					{!hideQuoteOptions && showFlightDetails && (
						<Collapse
							className="holiday-summary__collapse"
							ref={flightDetailsCollapseRef}
							expandIcon={({ isActive }) => {
								return (
									<IconArrowDown
										style={{
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="holiday-summary__collapse-header"
								header={
									<li
										className="holiday-summary__list-item"
										data-testid="detail-flight"
									>
										{booking?.flight?.type === TRANSPORTATION_TYPES.TRAIN ? (
											<IconNegoTransport />
										) : (
											<IconNegoPlane />
										)}
										{flightNegociatedLabel}
									</li>
								}
							>
								<div className="holiday-summary__collapse-content">
									<MyFlightDetail
										flight={booking.flight}
										lugaggeCount={booking.luggage}
										luggageMaxCount={luggageMaxCount}
										flightOptionsFlightCode={flightOptionsFlightCode}
										flightOptions={flightOptions}
									/>
								</div>
							</Collapse.Panel>
						</Collapse>
					)}

					{!hideQuoteOptions && transfer.vehicule && (
						<li className="holiday-summary__list-item" data-testid="detail-transfer">
							<IconNegoTransfer />
							<div>{transfer.vehicule}</div>
						</li>
					)}

					{/*!hideQuoteOptions && activities && <ActivitySummary activities={activities} />*/}

					{!isEmpty(nonIncludedActivitiesList) && (
						<div className="holiday-summary__collapse-content holiday-summary__collapse-content--activities-extra">
							{nonIncludedActivitiesList.map((activity, index) => {
								return (
									<li
										key={index}
										className="holiday-summary__list-item"
										data-testid="activities-extra"
									>
										<NegociatedItem label={activity.name} />
									</li>
								);
							})}
						</div>
					)}

					{!hideQuoteOptions && negotiatedListToDisplay.length > 0 && (
						<div className="holiday-summary__collapse-content holiday-summary__collapse-content--negotiated">
							{negotiatedListToDisplay.map((negotiated, index) => {
								return (
									<li
										key={index}
										className="holiday-summary__list-item"
										data-testid="detail-negotiated"
									>
										<NegociatedItem label={negotiated.label} />
									</li>
								);
							})}
						</div>
					)}
				</ul>
				{showStressMarketingMsg && stressMarketingLabel && (
					<div className="holiday-summary__stress-marketing">
						<div className="holiday-summary__stress-marketing--icon">
							<IconClockNotification width={16} heigth={16} />
						</div>
						<div className="holiday-summary__stress-marketing--label">
							{stressMarketingLabel}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

HolidaySummary.propTypes = {
	isSDP: PropTypes.bool,
	showFlightDetails: PropTypes.bool,
	showProductPicture: PropTypes.bool,
	booking: PropTypes.object,
	ficheProduit: PropTypes.object,
	showTimer: PropTypes.bool,
	showBookingBanner: PropTypes.bool,
	hideQuoteOptions: PropTypes.bool,
	luggageMaxCount: PropTypes.number,
	quotationStatus: PropTypes.string,
	negotiatedOfSelectedOffer: PropTypes.array,
	activities: PropTypes.arrayOf(PropTypes.object),
	stressMarketingMsg: PropTypes.object,
	flightOptions: PropTypes.object,
	flightOptionsFlightCode: PropTypes.string,
	priceChangeData: PropTypes.object,
	quotationCode: PropTypes.string,
};

export default memo(HolidaySummary);
